import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { getApiErrorMessage } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, MessageCircle, ArrowLeft, Check, KeyRound } from 'lucide-react';

const AUTH_IMAGE = "https://images.unsplash.com/photo-1761083042210-bb334df182e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkYXJrJTIwd29vZCUyMG9mZmljZSUyMGludGVyaW9yfGVufDB8fHx8MTc3MzY5NDY5MXww&ixlib=rb-4.1.0&q=85";

export default function RecoverPage() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite') === '1' ? (searchParams.get('code') || '') : '';
  const [step, setStep] = useState(inviteCode ? 'code' : 'choose'); // choose, email, telegram, code, done
  const [email, setEmail] = useState('');
  const [tgUsername, setTgUsername] = useState('');
  const [code, setCode] = useState(inviteCode);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState(inviteCode ? 'invite' : '');

  const sendByEmail = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/recover/email', { email });
      setMethod(data.method || 'email');
      toast.success(data.message);
      setStep('code');
    } catch (err) { toast.error(getApiErrorMessage(err, 'Не удалось отправить код')); }
    setLoading(false);
  };

  const sendByTelegram = async () => {
    if (!tgUsername.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/recover/telegram', { telegram_username: tgUsername });
      setMethod('telegram');
      toast.success(data.message);
      setStep('code');
    } catch (err) { toast.error(getApiErrorMessage(err, 'Не удалось отправить код')); }
    setLoading(false);
  };

  const resetPassword = async () => {
    if (!code.trim() || !newPassword.trim()) return;
    if (newPassword.length < 6) {
      toast.error('Пароль должен содержать не менее 6 символов');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { code, new_password: newPassword });
      toast.success('Пароль обновлён!');
      setStep('done');
    } catch (err) { toast.error(getApiErrorMessage(err, 'Неверный или истёкший код')); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${AUTH_IMAGE})` }} />
      <div className="absolute inset-0" style={{ background: 'rgba(12, 10, 9, 0.88)', backdropFilter: 'blur(4px)' }} />

      <div className="relative z-10 w-full max-w-md mx-4 animate-fade-in">
        <div className="bg-stone-900/90 border border-stone-800 rounded-xl p-8 shadow-2xl shadow-black/60 backdrop-blur-sm">
          <Link to="/" className="flex items-center gap-1 mb-6 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="" className="w-12 h-12 rounded-lg object-cover" />
            <span className="font-heading text-xl text-stone-200"><span className="text-stone-400">Pro</span><span className="text-amber-500">Praxis</span></span>
          </Link>

          {/* Step: Choose method */}
          {step === 'choose' && (
            <div>
              <h2 className="font-heading text-lg text-stone-200 mb-1">Восстановление доступа</h2>
              <p className="text-stone-500 text-sm mb-5">Выберите способ получения кода</p>
              <div className="space-y-3">
                <button onClick={() => setStep('email')}
                  className="w-full flex items-center gap-4 p-4 rounded-lg border transition-colors text-left hover:border-amber-700/50"
                  style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--app-accent-muted)' }}>
                    <Mail size={18} style={{ color: 'var(--app-accent)' }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>По email</p>
                    <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Введите email, на который регистрировались</p>
                  </div>
                </button>
                <button onClick={() => setStep('telegram')}
                  className="w-full flex items-center gap-4 p-4 rounded-lg border transition-colors text-left hover:border-amber-700/50"
                  style={{ background: 'var(--app-card-deep)', borderColor: 'var(--app-border)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--app-accent-muted)' }}>
                    <MessageCircle size={18} style={{ color: 'var(--app-accent)' }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>Через Telegram</p>
                    <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Если подключали бот уведомлений</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step: Email */}
          {step === 'email' && (
            <div>
              <button onClick={() => setStep('choose')} className="flex items-center gap-1 text-xs mb-4" style={{ color: 'var(--app-text-muted)' }}><ArrowLeft size={13} /> Назад</button>
              <h2 className="font-heading text-lg text-stone-200 mb-4">Введите email</h2>
              <div className="space-y-3">
                <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="email@example.com"
                  onKeyDown={e => e.key === 'Enter' && sendByEmail()}
                  className="bg-stone-950/50 border-stone-800 text-stone-200 h-11" autoFocus />
                <Button onClick={sendByEmail} disabled={loading || !email.trim()} className="w-full h-11 text-white" style={{ background: 'var(--app-accent)' }}>
                  {loading ? 'Отправка...' : 'Отправить код'}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Telegram */}
          {step === 'telegram' && (
            <div>
              <button onClick={() => setStep('choose')} className="flex items-center gap-1 text-xs mb-4" style={{ color: 'var(--app-text-muted)' }}><ArrowLeft size={13} /> Назад</button>
              <h2 className="font-heading text-lg text-stone-200 mb-4">Ваш Telegram</h2>
              <div className="space-y-3">
                <Input value={tgUsername} onChange={e => setTgUsername(e.target.value)} placeholder="@username"
                  onKeyDown={e => e.key === 'Enter' && sendByTelegram()}
                  className="bg-stone-950/50 border-stone-800 text-stone-200 h-11" autoFocus />
                <Button onClick={sendByTelegram} disabled={loading || !tgUsername.trim()} className="w-full h-11 text-white" style={{ background: 'var(--app-accent)' }}>
                  {loading ? 'Отправка...' : 'Отправить код в Telegram'}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Enter code + new password */}
          {step === 'code' && (
            <div>
              <div className="text-center mb-5">
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}>
                  <KeyRound size={22} style={{ color: 'var(--app-accent)' }} />
                </div>
                <h2 className="font-heading text-lg text-stone-200 mb-1">{method === 'invite' ? 'Примите приглашение' : 'Введите код'}</h2>
                <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                  {method === 'invite'
                    ? 'Придумайте пароль для входа в организацию. Код из письма уже заполнен.'
                    : `Код отправлен ${method === 'telegram' ? 'в Telegram' : 'на email'}. Действителен 15 минут.`}
                </p>
              </div>
              <div className="space-y-3">
                {method !== 'invite' && (
                  <Input value={code} onChange={e => setCode(e.target.value)} placeholder="6-значный код"
                    className="bg-stone-950/50 border-stone-800 text-stone-200 h-11 text-center text-lg tracking-widest" maxLength={6} autoFocus />
                )}
                <Input value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password" placeholder="Новый пароль"
                  onKeyDown={e => e.key === 'Enter' && resetPassword()}
                  minLength={6} className="bg-stone-950/50 border-stone-800 text-stone-200 h-11" autoFocus={method === 'invite'} />
                <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Не менее 6 символов</p>
                <Button onClick={resetPassword} disabled={loading || !code.trim() || newPassword.length < 6} className="w-full h-11 text-white" style={{ background: 'var(--app-accent)' }}>
                  {loading ? 'Сброс...' : 'Сбросить пароль'}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Done */}
          {step === 'done' && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}>
                <Check size={28} style={{ color: 'var(--app-accent)' }} />
              </div>
              <h2 className="font-heading text-xl text-stone-200 mb-2">Пароль обновлён!</h2>
              <Link to="/login"><Button className="mt-4 text-white" style={{ background: 'var(--app-accent)' }}>Войти</Button></Link>
            </div>
          )}

          {step === 'choose' && (
            <p className="text-stone-600 text-sm text-center mt-6">
              Вспомнили? <Link to="/login" className="text-amber-500 hover:text-amber-400">Войти</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
